import { Box, Typography, TextField, Button, Stack } from '@mui/material';
import { SurveyQuestion } from '../../types/survey';

interface SurveyPreviewQuestionsProps {
  questions: SurveyQuestion[];
}

const SurveyPreviewQuestions = ({ questions }: SurveyPreviewQuestionsProps) => {
  const renderQuestionInput = (question: SurveyQuestion, index: number) => {
    const questionNumber = index + 1;

    switch (question.answerType) {
      case 'rating':
        return (
          <Box key={question.id} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                fontSize: '0.875rem',
                color: 'text.primary'
              }}
            >
              {questionNumber}. {question.question}
              {question.isMandatory && (
                <Typography component="span" sx={{ color: '#EF4444', ml: 0.5 }}>
                  *
                </Typography>
              )}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {[1, 2, 3, 4, 5].map((rating) => (
                <Button
                  key={rating}
                  variant="outlined"
                  sx={{
                    minWidth: 40,
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    border: '1px solid #D1D5DB',
                    color: 'text.secondary',
                    '&:hover': {
                      backgroundColor: 'rgba(74, 144, 226, 0.1)',
                      borderColor: '#4A90E2',
                      color: '#4A90E2'
                    }
                  }}
                >
                  {rating}
                </Button>
              ))}
            </Box>
          </Box>
        );

      case 'text':
        return (
          <Box key={question.id} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                fontSize: '0.875rem',
                color: 'text.primary'
              }}
            >
              {questionNumber}. {question.question}
              {question.isMandatory && (
                <Typography component="span" sx={{ color: '#EF4444', ml: 0.5 }}>
                  *
                </Typography>
              )}
            </Typography>
            <TextField
              multiline
              rows={3}
              placeholder="Your answer here..."
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'white',
                  fontSize: '0.875rem'
                }
              }}
            />
          </Box>
        );

      case 'number':
        return (
          <Box key={question.id} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                fontSize: '0.875rem',
                color: 'text.primary'
              }}
            >
              {questionNumber}. {question.question}
              {question.isMandatory && (
                <Typography component="span" sx={{ color: '#EF4444', ml: 0.5 }}>
                  *
                </Typography>
              )}
            </Typography>
            <TextField
              type="number"
              placeholder={question.placeholder || "Enter a number from 1-10"}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'white',
                  fontSize: '0.875rem'
                }
              }}
            />
          </Box>
        );

      case 'yesno':
        return (
          <Box key={question.id} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                fontSize: '0.875rem',
                color: 'text.primary'
              }}
            >
              {questionNumber}. {question.question}
              {question.isMandatory && (
                <Typography component="span" sx={{ color: '#EF4444', ml: 0.5 }}>
                  *
                </Typography>
              )}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                sx={{
                  flex: 1,
                  height: 40,
                  border: '1px solid #D1D5DB',
                  color: 'text.secondary',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: 'rgba(74, 144, 226, 0.1)',
                    borderColor: '#4A90E2',
                    color: '#4A90E2'
                  }
                }}
              >
                Yes
              </Button>
              <Button
                variant="outlined"
                sx={{
                  flex: 1,
                  height: 40,
                  border: '1px solid #D1D5DB',
                  color: 'text.secondary',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: 'rgba(74, 144, 226, 0.1)',
                    borderColor: '#4A90E2',
                    color: '#4A90E2'
                  }
                }}
              >
                No
              </Button>
            </Box>
          </Box>
        );

      default:
        return null;
    }
  };

  if (questions.length === 0) {
    return null;
  }

  return (
    <Stack spacing={3}>
      {questions.map((question, index) => renderQuestionInput(question, index))}
    </Stack>
  );
};

export default SurveyPreviewQuestions;

